import { useContext, useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { CatalogContext } from "./CatalogContext";
import { CatalogControlGroup } from "./CatalogControlGroup";
import { getOptionsBySlug } from "./options";
import { useFilter } from "./hooks";

type CatalogSelectionValue = Record<string, number>;
type HistoryState = {
  catalog: {
    rootNodeId?: string;
    value: CatalogSelectionValue;
  };
};

export const Catalog = ({
  activity,
  columns = 3,
  rows = 2,
}: {
  activity: Travelguide.Activity;
  columns?: number;
  rows?: number;
}) => {
  const initialLimit = rows * columns;
  const [limit, setLimit] = useState(initialLimit);
  const [value, setValue] = useState<CatalogSelectionValue>({});
  const options = getOptionsBySlug(activity);

  const filteredData = useFilter(value, options);
  const slicedData = filteredData.slice(0, limit);
  const finishedLoading = slicedData.length === filteredData.length;

  const gridSizes = [
    "grid-cols-1",
    "grid-cols-1 sm:grid-cols-2",
    "grid-cols-1 sm:grid-cols-3",
  ];

  const { rootNodeId } = useContext(CatalogContext);

  function pushHistory(value: CatalogSelectionValue) {
    const newState: HistoryState = { catalog: { rootNodeId, value } };
    history.pushState(newState, document.title);
  }

  function handleOnChange({
    name,
    selectedIndex,
  }: {
    name: string;
    selectedIndex: number;
  }) {
    setValue((pending) => {
      const newValue = { ...pending, [name]: selectedIndex };
      pushHistory(newValue);
      return newValue;
    });
    setLimit(initialLimit);
  }

  function handleOnReset(): void {
    setValue(() => {
      const newValue = {};
      pushHistory(newValue);
      return newValue;
    });
    setLimit(initialLimit);
  }

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state: HistoryState | Record<string, never> = event.state;
      if (state?.catalog?.value) {
        setValue(state.catalog.value);
      }
    };

    window.addEventListener("popstate", handlePopState);

    const currentState = window.history?.state;
    if (
      currentState &&
      currentState.catalog &&
      currentState.catalog.rootNodeId === rootNodeId
    ) {
      setValue(currentState.catalog.value);
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return (
    <div className="flex flex-col gap-y-6">
      {options && (
        <CatalogControlGroup
          options={options}
          value={value}
          onChange={handleOnChange}
          onReset={handleOnReset}
        />
      )}
      {slicedData.length ? (
        <div className={`grid gap-4 auto-rows-min ${gridSizes[columns - 1]}`}>
          {slicedData.map((item, i) => (
            <Card key={i} item={item} showText={false} />
          ))}
        </div>
      ) : (
        <i className="text-center">Keine Einträge gefunden</i>
      )}
      {!finishedLoading && (
        <Button center onClick={() => setLimit(limit + rows * columns)}>
          Mehr laden
        </Button>
      )}
    </div>
  );
};
